/**
 * Unified plate -> contract matching ("who had this car at that moment").
 *
 * This is the single source of truth for every module that needs to attach a
 * contract / customer to a plate at a point in time (traffic fines, parking,
 * Salik, mileage, multi-car). It is a faithful port of the Python fines engine
 * (fines_pipeline/loaders.py + matcher.py) so both sides agree.
 *
 * Rules
 *  1. The E-Check log is the primary source. For every (contract, plate) pair a
 *     check-out event opens a possession segment and the next check-in closes it.
 *  2. The first segment of a contract starts at the contract's Pick-up Date (the
 *     customer signs before the car is inspected), never at the first E-Check.
 *  3. A replacement car's segment starts the moment the previous car was checked
 *     in (segments of one contract are contiguous).
 *  4. A segment without a check-in ends at the contract's drop-off date when the
 *     contract is closed, otherwise it is open until now.
 *  5. Plates the E-Check log never saw fall back to the contract's own window.
 *  6. When two windows cover the same moment, exact E-Check windows beat
 *     contract-only windows; among equals the latest start wins.
 *  7. Contract records may come from the airport export ("Customer",
 *     "Customer Name", "Pick-up Date" ...) or the Business Bay collection
 *     (snake_case: customer, pickup_date ...); both are accepted.
 *
 * Everything in here is pure (no Firestore); see contractLookup.js for I/O.
 */

export const SOURCE_ECHECK = 'echeck';
export const SOURCE_CONTRACT = 'contract';
export const UNKNOWN_CUSTOMER = 'UNKNOWN / CONTRACT NOT IN CONTRACTS FILE';
export const OPEN_STATUSES = ['open'];
export const EXCLUDED_STATUSES = ['cancelled'];

// Newest "Last Update" wins when a contract exists in several places; on a tie
// the most advanced status wins (a contract never goes back to Open).
const STATUS_RANK = { open: 0, delivered: 1, 'delivered-pending': 1, closed: 2, 'delivered-indebt': 2, 'delivered-paid': 3, cancelled: 3 };

// ---------------------------------------------------------------- helpers
export const cleanText = (v) => (v === null || v === undefined ? '' : String(v).trim());

/** "T 36801" -> "T36801", "dd-37250" -> "DD37250", 36801.0 -> "36801". */
export const normalizePlate = (v) => {
    let t = cleanText(v).toUpperCase();
    if (/^-?\d+\.0+$/.test(t)) t = t.split('.')[0];
    return t.replace(/[^A-Z0-9]/g, '');
};

const pad = (n) => String(n).padStart(2, '0');

/**
 * Parse the date formats seen across yelo exports and Firestore:
 *  - Date objects / Firestore Timestamps ({ seconds } or .toDate())
 *  - "03/07/2024 16:28" (day-first, the contract & E-Check exports)
 *  - "1/24/2024 6:53:10 PM +04:00" (month-first with offset, old airport docs)
 *  - "2026-09-08T17:02:00.000Z" / "2026-09-08 17:02" (ISO)
 *  - Excel serial numbers (e.g. 45123.65)
 * Returns a local-time Date or null. Dubai has no DST, so an offset string is
 * converted to Gulf time (+04:00) and the wall clock is kept.
 */
export function parseDateTime(value) {
    if (value === null || value === undefined || value === '') return null;
    if (value instanceof Date) return isNaN(value) ? null : value;
    if (typeof value === 'object') {
        if (typeof value.toDate === 'function') return value.toDate();
        if (typeof value.seconds === 'number') return new Date(value.seconds * 1000);
        return null;
    }
    const text = String(value).trim();
    if (!text) return null;

    let m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/.exec(text);
    if (m) { // day-first (UAE convention)
        const [, d, mo, y, h = 0, mi = 0, s = 0] = m;
        return local(+y, +mo, +d, +h, +mi, +s);
    }
    m = /^(\d{1,2})\/(\d{1,2})\/(\d{4}) (\d{1,2}):(\d{2}):(\d{2}) ([AP]M) ([+-])(\d{2}):(\d{2})$/i.exec(text);
    if (m) { // month-first with 12h clock and offset -> Gulf wall clock
        const [, mo, d, y, h12, mi, s, ap, sign, oh, om] = m;
        let h = (+h12) % 12; if (ap.toUpperCase() === 'PM') h += 12;
        const utc = Date.UTC(+y, +mo - 1, +d, h, +mi, +s) - (sign === '-' ? -1 : 1) * ((+oh) * 60 + (+om)) * 60000;
        const gulf = new Date(utc + 4 * 3600000);
        return local(gulf.getUTCFullYear(), gulf.getUTCMonth() + 1, gulf.getUTCDate(), gulf.getUTCHours(), gulf.getUTCMinutes(), gulf.getUTCSeconds());
    }
    m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?(Z|[+-]\d{2}:?\d{2})?)?$/.exec(text);
    if (m) {
        const [, y, mo, d, h = 0, mi = 0, s = 0, tz] = m;
        if (!tz) return local(+y, +mo, +d, +h, +mi, +s);
        const gulf = new Date(new Date(text).getTime() + 4 * 3600000);
        return local(gulf.getUTCFullYear(), gulf.getUTCMonth() + 1, gulf.getUTCDate(), gulf.getUTCHours(), gulf.getUTCMinutes(), gulf.getUTCSeconds());
    }
    if (/^\d{5}(\.\d+)?$/.test(text)) { // Excel serial
        const ms = Math.round((parseFloat(text) - 25569) * 86400000);
        const u = new Date(ms);
        return local(u.getUTCFullYear(), u.getUTCMonth() + 1, u.getUTCDate(), u.getUTCHours(), u.getUTCMinutes(), u.getUTCSeconds());
    }
    return null;
}

function local(y, mo, d, h, mi, s) {
    const dt = new Date(y, mo - 1, d, h, mi, s, 0);
    return isNaN(dt) ? null : dt;
}

export const formatDateTime = (d) => (d ? `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}` : '');

// ------------------------------------------------------ contract records
const first = (doc, ...keys) => {
    for (const k of keys) {
        const v = doc[k];
        if (v !== undefined && v !== null && v !== '') return v;
    }
    return '';
};

/**
 * Bring any contract document (airport export keys or Business Bay snake_case)
 * to one shape. Returns null when the record is unusable (cancelled, no plate,
 * no pick-up date).
 */
export function normalizeContract(doc, now = new Date()) {
    if (!doc) return null;
    const status = cleanText(first(doc, 'Status', 'status'));
    if (EXCLUDED_STATUSES.includes(status.toLowerCase())) return null;
    const plateRaw = cleanText(first(doc, 'Plate No.', 'plate_no', 'Plate Number', 'plate'));
    const plate = normalizePlate(plateRaw);
    if (!plate) return null;
    const start = parseDateTime(first(doc, 'Pick-up Date', 'pickup_date'));
    if (!start) return null;
    const plannedEnd = parseDateTime(first(doc, 'Drop-off Date', 'dropoff_date', 'Close Date', 'close_date'));
    const isOpen = OPEN_STATUSES.includes(status.toLowerCase()) || !plannedEnd;
    return {
        contractNo: cleanText(first(doc, 'Contract No.', 'contract_no', 'Contract No', 'Agreement')).toUpperCase(),
        customer: cleanText(first(doc, 'Customer', 'customer', 'Customer Name', 'customer_name')),
        branch: cleanText(first(doc, 'Pick-up Branch', 'pickup_branch', 'Branch')) || 'Unknown_Branch',
        plateRaw,
        plate,
        start,
        end: isOpen ? now : plannedEnd,
        plannedEnd,
        status,
        isOpen,
        lastUpdate: parseDateTime(first(doc, 'Last Update', 'last_update', 'last_updated')),
    };
}

/** Keep one record per contract number: newest Last Update, then highest status. */
export function dedupeContracts(records) {
    const best = new Map();
    const names = new Map();
    for (const r of records) {
        if (!r) continue;
        if (r.customer) names.set(r.contractNo, r.customer);
        const cur = best.get(r.contractNo);
        if (!cur || better(r, cur)) best.set(r.contractNo, r);
    }
    for (const r of best.values()) { // the winning copy may lack the name the other copy had
        if (!r.customer && names.has(r.contractNo)) r.customer = names.get(r.contractNo);
    }
    return [...best.values()];
}

function better(a, b) {
    const ta = a.lastUpdate ? a.lastUpdate.getTime() : -1;
    const tb = b.lastUpdate ? b.lastUpdate.getTime() : -1;
    if (ta !== tb) return ta > tb;
    return (STATUS_RANK[a.status.toLowerCase()] ?? 1) > (STATUS_RANK[b.status.toLowerCase()] ?? 1);
}

// --------------------------------------------------------- E-Check events
export const CHECK_CREATE = 'create';
export const CHECK_CLOSE = 'close';

export function checkKind(value) {
    const t = cleanText(value).toLowerCase();
    if (['create', 'open', 'check out', 'checkout', 'hand over'].some(k => t.includes(k))) return CHECK_CREATE;
    if (['close', 'return', 'check in', 'checkin'].some(k => t.includes(k))) return CHECK_CLOSE;
    return null;
}

/**
 * Turn raw E-Check rows into possession segments
 *   { contractNo, plate, plateRaw, start, end, branch, open }
 * `contracts` = normalized contract records (used for contract start/end).
 */
// Ejar's E-Check number (the store's check id when there is none): the order of two checks in the SAME minute - the
// archive keeps minutes and comes newest first (owner, 3 Oct 2026, CC 79361: created 17:37:19, closed 17:37:59 - read
// the other way round, the car looked kept to the contract's end and the next customer's fine named two contracts)
const echeckSeq = (row) => {
    const n = Number(cleanText(row['E-Check Number'] ?? row.echeck_number ?? row.check_number));
    if (Number.isFinite(n) && n > 0) return n;
    const id = Number(row.check_id);
    return Number.isFinite(id) ? id : 0;
};

export function buildSegments(rows, contracts, now = new Date()) {
    const startOf = new Map();
    const endOf = new Map();
    for (const c of contracts) {
        if (!c) continue;
        startOf.set(c.contractNo, c.start);
        endOf.set(c.contractNo, c.end);
    }
    const events = [];
    for (const row of rows || []) {
        const when = parseDateTime(row['Transaction Date'] ?? row.transaction_date ?? row.date);
        const kind = checkKind(row['Check Type'] ?? row.check_type ?? row.type);
        const contractNo = cleanText(row['Contract No.'] ?? row.contract_no ?? row.contract).toUpperCase();
        const plateRaw = cleanText(row['Plate Number'] ?? row['Plate No.'] ?? row.plate_number ?? row.plate);
        const plate = normalizePlate(plateRaw);
        if (!when || !kind || !contractNo || !plate) continue;
        events.push({ contractNo, plate, plateRaw, when, kind, branch: cleanText(row.Branch ?? row.branch), seq: echeckSeq(row) });
    }
    events.sort((a, b) => a.contractNo.localeCompare(b.contractNo) || a.plate.localeCompare(b.plate) || (a.when - b.when) || (a.seq - b.seq));

    const segments = [];
    let key = null, openStart = null, openBranch = '', plateRaw = '';
    const flush = () => {
        if (key && openStart) {
            const [contractNo, plate] = key;
            let end = endOf.has(contractNo) ? endOf.get(contractNo) : now;
            if (end < openStart) end = now;
            segments.push({ contractNo, plate, plateRaw, start: openStart, end, branch: openBranch, open: end === now });
        }
        openStart = null;
    };
    for (const ev of events) {
        const k = [ev.contractNo, ev.plate];
        if (!key || key[0] !== k[0] || key[1] !== k[1]) {
            flush();
            key = k; plateRaw = ev.plateRaw; openBranch = '';
        }
        if (ev.kind === CHECK_CREATE) {
            if (openStart) segments.push({ contractNo: ev.contractNo, plate: ev.plate, plateRaw, start: openStart, end: ev.when, branch: openBranch, open: false });
            openStart = ev.when; openBranch = ev.branch;
        } else {
            let start = openStart;
            if (!start) { // check-in without check-out: held since the contract started
                start = startOf.get(ev.contractNo);
                if (!start || start > ev.when) continue;
            }
            segments.push({ contractNo: ev.contractNo, plate: ev.plate, plateRaw, start, end: ev.when, branch: openBranch || ev.branch, open: false });
            openStart = null;
        }
    }
    flush();
    return alignWithContracts(segments, startOf);
}

/** First segment starts at contract pick-up; consecutive segments are contiguous. */
function alignWithContracts(segments, startOf) {
    segments.sort((a, b) => a.contractNo.localeCompare(b.contractNo) || a.start - b.start);
    let prevContract = null, prevEnd = null;
    for (const s of segments) {
        if (s.contractNo !== prevContract) {
            const opened = startOf.get(s.contractNo);
            if (opened && opened < s.start) s.start = opened;
            prevContract = s.contractNo; prevEnd = s.end;
            continue;
        }
        if (prevEnd && prevEnd < s.start) s.start = prevEnd;
        prevEnd = prevEnd && prevEnd > s.end ? prevEnd : s.end;
    }
    return segments;
}

// ------------------------------------------------------- possession index
/**
 * Windows grouped by plate, newest start first:
 *   { contractNo, customer, branch, start, end, isOpen, source, customerKnown,
 *     plannedEnd, contractStart, contractEnd }
 */
export function buildWindows(contracts, segments) {
    const byContract = new Map();
    for (const c of contracts) if (c) byContract.set(c.contractNo, c);
    const index = new Map();
    const loggedPlates = new Map();
    const lastSegmentEnd = new Map();
    const push = (plate, w) => { if (!index.has(plate)) index.set(plate, []); index.get(plate).push(w); };

    for (const seg of segments || []) {
        const info = byContract.get(seg.contractNo);
        push(seg.plate, info ? {
            contractNo: seg.contractNo, customer: info.customer, branch: info.branch,
            start: seg.start, end: seg.end, isOpen: info.isOpen, source: SOURCE_ECHECK, customerKnown: true,
            plannedEnd: info.plannedEnd, contractStart: info.start, contractEnd: info.isOpen ? null : info.end,
        } : {
            contractNo: seg.contractNo, customer: UNKNOWN_CUSTOMER, branch: seg.branch || 'Unassigned_Fines',
            start: seg.start, end: seg.end, isOpen: seg.open, source: SOURCE_ECHECK, customerKnown: false,
            plannedEnd: null, contractStart: seg.start, contractEnd: seg.open ? null : seg.end,
        });
        if (!loggedPlates.has(seg.contractNo)) loggedPlates.set(seg.contractNo, new Set());
        loggedPlates.get(seg.contractNo).add(seg.plate);
        const prev = lastSegmentEnd.get(seg.contractNo);
        if (!prev || seg.end > prev) lastSegmentEnd.set(seg.contractNo, seg.end);
    }
    for (const c of contracts) {
        if (!c) continue;
        const plates = loggedPlates.get(c.contractNo);
        if (plates && plates.has(c.plate)) continue; // history fully described by the E-Check log
        let start = c.start;
        if (plates) start = new Date(Math.max(start, lastSegmentEnd.get(c.contractNo))); // a plate the log never saw can only be the latest
        if (start > c.end) continue;
        push(c.plate, {
            contractNo: c.contractNo, customer: c.customer, branch: c.branch,
            start, end: c.end, isOpen: c.isOpen, source: SOURCE_CONTRACT, customerKnown: true,
            plannedEnd: c.plannedEnd, contractStart: c.start, contractEnd: c.isOpen ? null : c.end,
        });
    }
    for (const list of index.values()) list.sort((a, b) => b.start - a.start);
    return index;
}

/** The window covering `moment` for one plate: { window, note }. */
export function findWindow(windows, moment) {
    const hits = (windows || []).filter(w => w.start <= moment && moment <= w.end);
    if (!hits.length) return { window: null, note: '' };
    const exact = hits.filter(w => w.source === SOURCE_ECHECK);
    const pool = exact.length ? exact : hits;
    if (pool.length === 1 && hits.length === 1) return { window: pool[0], note: '' };
    const chosen = pool[0];
    const others = hits.filter(w => w !== chosen).map(w => w.contractNo).join(', ');
    return { window: chosen, note: `Overlapping windows also covering this time: ${others}` };
}

/**
 * One-stop matcher. `contractDocs` = raw Firestore/Excel contract records,
 * `echeckRows` = raw E-Check rows. Returns an object with `match(plate, when)`.
 */
export function createMatcher({ contractDocs = [], echeckRows = [], now = new Date() } = {}) {
    const contracts = dedupeContracts(contractDocs.map(d => normalizeContract(d, now)));
    const segments = buildSegments(echeckRows, contracts, now);
    const index = buildWindows(contracts, segments);
    const byContract = new Map(contracts.map(c => [c.contractNo, c]));
    return {
        contracts,
        segments,
        index,
        /** Contract holding `plate` at `when` (Date or parseable string). */
        match(plate, when) {
            const moment = when instanceof Date ? when : parseDateTime(when);
            const key = normalizePlate(plate);
            const windows = index.get(key) || [];
            if (!moment) return { matched: false, note: 'No date/time on the record', windows };
            const { window, note } = findWindow(windows, moment);
            if (!window) {
                return { matched: false, windows,
                    note: windows.length ? 'No contract held the plate at this time' : 'Plate not in contracts or E-Check log' };
            }
            return { matched: true, windows, note: window.customerKnown ? note
                : `Contract not in contracts data; customer unknown (E-Check log)${note ? `. ${note}` : ''}`,
                ...window };
        },
        /** Every possession segment of one contract (car swaps included), oldest first. */
        segmentsOf(contractNo) {
            const no = cleanText(contractNo).toUpperCase();
            const own = segments.filter(s => s.contractNo === no);
            if (own.length) return own.slice().sort((a, b) => a.start - b.start);
            const c = byContract.get(no); // never inspected: the contract's own window
            return c ? [{ contractNo: no, plate: c.plate, plateRaw: c.plateRaw, start: c.start, end: c.end, branch: c.branch, open: c.isOpen }] : [];
        },
        contract(contractNo) { return byContract.get(cleanText(contractNo).toUpperCase()) || null; },
        /** Odometer / E-Check numbers at the two ends of one segment (see segmentReadings). */
        readings(segment) { return segmentReadings(echeckRows, segment); },
    };
}

// ------------------------------------------------------------- mileage
const num = (v) => { const n = parseFloat(String(v ?? '').replace(/[^\d.-]/g, '')); return isNaN(n) ? null : n; };

/**
 * The check-out and check-in rows that bound one possession segment, with their
 * odometer readings. A plate used twice in a contract (A -> B -> A) therefore
 * gets two independent readings and is counted twice, as it should be.
 * Returns { out: {when, odometer, echeckNo} | null, in: {...} | null, km }.
 */
export function segmentReadings(rows, segment) {
    if (!segment) return { out: null, in: null, km: null };
    const contractNo = cleanText(segment.contractNo).toUpperCase();
    const plate = normalizePlate(segment.plate);
    const own = [];
    for (const row of rows || []) {
        if (cleanText(row['Contract No.'] ?? row.contract_no ?? row.contract).toUpperCase() !== contractNo) continue;
        if (normalizePlate(row['Plate Number'] ?? row['Plate No.'] ?? row.plate_number ?? row.plate) !== plate) continue;
        const when = parseDateTime(row['Transaction Date'] ?? row.transaction_date ?? row.date);
        const kind = checkKind(row['Check Type'] ?? row.check_type ?? row.type);
        if (!when || !kind || when < segment.start || when > segment.end) continue;
        own.push({ when, kind, odometer: num(row.Odometer ?? row.ODO ?? row.KM ?? row.odometer), echeckNo: cleanText(row['E-Check Number'] ?? row.echeck_number), seq: echeckSeq(row) });
    }
    own.sort((a, b) => (a.when - b.when) || (a.seq - b.seq));
    const out = own.find(r => r.kind === CHECK_CREATE) || null;
    const back = [...own].reverse().find(r => r.kind === CHECK_CLOSE) || null;
    const km = out && back && out.odometer !== null && back.odometer !== null && back.odometer >= out.odometer ? back.odometer - out.odometer : null;
    return { out, in: back, km };
}
